import React from "react";
import { useQuery } from "@apollo/client";
import { useParams, Link } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Card from "react-bootstrap/Card";

import { QUERY_USER_ACTIVITY_LOG } from "../../../config/graphql/query";

const UserActivityLog = React.memo(() => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["userActivity", "common"]);

  const { data } = useQuery<{ userActivityLog?: IUserActivityLog }>(
    QUERY_USER_ACTIVITY_LOG,
    {
      skip: !id,
      variables: { id },
    },
  );

  const log = data?.userActivityLog;
  const user = log?.user;

  const roles =
    user?.roles.map((role) =>
      role === "CLIENT_ADMIN" &&
      ["distributor", "reseller"].includes(user.client.type)
        ? user.client.type.toUpperCase()
        : role,
    ) ?? [];

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">
            <Link to="/userActivityLogs">
              {t("userActivity:nav.userActivityLogs")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {t("userActivity:nav.userActivityLog")}
          </li>
        </ol>
      </nav>

      {!!user && (
        <Card className="mb-3 p-3">
          {t("userActivity:log.card.fullName")}
          {`${user.firstName || ""} ${user.lastName || ""}`}
        </Card>
      )}

      <Card className="mb-3 p-3">
        {t("userActivity:log.card.email")}
        {user?.email || log?.metadata?.email || ""}
      </Card>

      {!!user && (
        <>
          <Card className="mb-3 p-3">
            {t("userActivity:log.card.roles")}
            {roles.join(" , ")}
          </Card>
          <Card className="mb-3 p-3">
            {t("userActivity:log.card.client")}
            {user.client.title}
          </Card>
        </>
      )}

      <Card className="mb-3 p-3">
        {t("userActivity:log.card.action")}
        {log?.action?.toUpperCase()}
      </Card>

      <Card className="mb-3 p-3">
        {t("userActivity:log.card.status")}
        {log?.status?.toUpperCase()}
      </Card>

      {log?.status === "failure" && (
        <Card className="mb-3 p-3">
          {t("userActivity:log.card.reason")}
          {log?.metadata?.reason || "Unknown"}
        </Card>
      )}

      <Card className="mb-3 p-3">
        {t("userActivity:log.card.2fa")}
        {log?.isTwoFactorAuth?.toString().toUpperCase()}
      </Card>

      <Card className="mb-3 p-3">
        {t("userActivity:log.card.ip")}
        {log?.ipAddress}
      </Card>

      <Card className="mb-3 p-3">
        {t("userActivity:log.card.userAgent")}
        {log?.userAgent}
      </Card>

      <Card className="mb-3 p-3">
        {t("userActivity:log.card.created")}
        {log?.createdAt
          ? dayjs(log.createdAt).local().format("DD-MM-YYYY / HH:mm")
          : ""}
      </Card>
    </div>
  );
});

export default UserActivityLog;
